import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import _ from 'lodash';
import {
  BLOOD_GLUCOSE,
  BLOOD_PRESSURE,
  BODY_COMPOSITION,
  OXYGEN_AND_PULSE_RATE,
  TEMPERATURE,
} from '../../enums/enums';

export interface IService {
  is_mandatory: boolean;
  service_id: number;
  service_name: string;
}

export interface IVitalSign {
  property_name: string;
  property_id_out: number;
  sample_date: Date;
  datetime_measure: Date;
  vital_sign_group_id: string;
  vital_sign_group_name: string;
  vital_sign_id: string;
  vital_sign_master_id?: string;
  vital_sign_name: string;
  account_vital_sign_external_id?: number;
  vital_sign_uom_id: number;
  vital_sign_long_description: string;
  vital_sign_short_description: string;
  vital_sign_group_id_out: number;
  product_order_item_id: number;
  color_code: string;
  product_order_id: number;
  property_value: string | number;
  uom: string;
  services: IService[];
  reference_range_name: string;
  maximum_value: string;
  minimum_value: string;
  biomarker_uom: string;
  mealtime_id?: string | number;
  biomarker_uom_id?: string | number;
  reference_introductory_statement?: string;
  reference_summary?: string;
  vital_sign_references: Array<{
    biomarker_uom_id: string | null;
    mealtime_id: string | null;
    maximum_value: number;
    minimum_value: number;
  }>;
  property_uom_id?: string | number;
  result_height?: string;
  result_weight?: string;
}
export interface VitalSignsState {
  status: string;
  type: string;
  vitalSigns: IVitalSign[];
  vitalSignResults: GroupedData;
  lastVitalSigns: GroupedData;
  noRecordVitalSigns: { groupId: number; groupName: string }[];
  mesurmentPlan: MeasurementPlanItem[];
  customerVitalSigns: any[];
}
interface MeasurementPlanItem {
  measurement_plan_notification_id: number;
  reminder: boolean;
  time_of_execution: string; // Format: "HH:mm:ss"
  day_of_execution: string; // Day name like "Monday", "Tuesday", etc.
  is_morning: boolean;
  is_bedtime: boolean;
}
interface GroupedData {
  [key: string]: IVitalSign[];
}
interface FetchVitalSignPayload {
  account_number: string | undefined;
  vital_sign_group_id_out?: number;
}

const initialState: VitalSignsState = {
  status: 'idle',
  type: '',
  vitalSigns: [],
  vitalSignResults: {},
  lastVitalSigns: {},
  mesurmentPlan: [],
  noRecordVitalSigns: [],
  customerVitalSigns: [],
};

interface MeasurementPlanItem {
  measurement_plan_notification_id: number;
  reminder: boolean;
  time_of_execution: string;
  day_of_execution: string;
  is_morning: boolean;
  is_bedtime: boolean;
}

interface MeasurementPlanRequest {
  measurement_plan: MeasurementPlanItem[];
}

export const fetchVitalSigns = createAsyncThunk(
  'vitalSigns/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const vitalSignsResponse: any = await client.get('/healthpoint/vital-signs');
      return vitalSignsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchVitalSignResult = createAsyncThunk(
  'vitalSigns/fetchVitalSignResult',
  async (
    { account_number, vital_sign_group_id_out }: FetchVitalSignPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const vitalSignsResponse: any = await client.get(
        `/core/healthpoint/customer/${account_number}/vital_sign${
          vital_sign_group_id_out ? `?vital_sign_group_id=${vital_sign_group_id_out}` : ''
        }`
      );
      return vitalSignsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const postMeasurmentPlan = createAsyncThunk(
  'post_measurement_plan',
  async (
    {
      data,
      vital_sign_group_id_out,
      personId,
    }: { data: MeasurementPlanRequest; vital_sign_group_id_out: number; personId: number },
    thunkAPI
  ) => {
    try {
      const res: any = await client.post(
        `/core/measurement_plan/${vital_sign_group_id_out}/${personId}`,
        data
      );
      return res;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data?.error_text ?? {});
    }
  }
);

export const getMeasurmentPlan = createAsyncThunk(
  'get_measurement_plan',
  async (
    { vital_sign_group_id_out, personId }: { vital_sign_group_id_out: number; personId: number },
    thunkAPI
  ) => {
    try {
      const res: any = await client.get(
        `/core/measurement_plan/${vital_sign_group_id_out}/${personId}`
      );
      return res;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const updateVitalSingByUserProfile = createAsyncThunk(
  'vitalSigns/updateVitalSignByUserProfile',
  async ({ data, account_number }: { data: any; account_number: string | undefined }, thunkAPI) => {
    try {
      const res: any = await client.post(`/core/account/${account_number}/vital_sign_result`, data);
      return res;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const getVitalSignByUserProfile = createAsyncThunk(
  'vitalSigns/getVitalSignByUserProfile',
  async ({ groupId, personId }: { groupId: number; personId?: number }, thunkAPI) => {
    try {
      const res: any = await client.get(`/core/measurement_plan/${groupId}/${personId}`);
      return res;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const vitalSignsSlice = createSlice({
  name: 'vitalSigns',
  initialState,
  reducers: {
    resetVitalSigns: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVitalSigns.pending, (state) => {
        state.status = 'pending';
        state.type = fetchVitalSigns.typePrefix;
      })
      .addCase(fetchVitalSigns.fulfilled, (state, action) => {
        state.vitalSigns = action.payload.data.vitalSigns;
        state.status = 'fulfilled';
        state.type = fetchVitalSigns.typePrefix;
      })
      .addCase(fetchVitalSigns.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
        state.type = fetchVitalSigns.typePrefix;
      })
      .addCase(fetchVitalSignResult.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchVitalSignResult.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        const { vital_sign_group_id_out } = action.meta.arg;
        const response = action.payload.data || [];
        const sortedResponse = response.sort(
          (a: any, b: any) =>
            new Date(b.datetime_measure).getTime() - new Date(a.datetime_measure).getTime()
        );
        const data = vital_sign_group_id_out
          ? response
          : _.uniqBy(sortedResponse, 'property_id_out');
        const latestChangesForEachGroup: any = [];
        const groupByGroupId = _.groupBy(data, 'vital_sign_group_id_out');
        Object.entries(groupByGroupId).forEach(([id, res]) => {
          const latestChange = res.sort(
            (a, b) =>
              new Date(b.datetime_measure).getTime() - new Date(a.datetime_measure).getTime()
          )[0];
          latestChangesForEachGroup.push(latestChange);
        });

        const groupedByGroupName: { [key: string]: any } = {};
        latestChangesForEachGroup.forEach((item: any) => {
          const filteredData = data.filter((el: any) => {
            return (
              el.product_order_id === item.product_order_id &&
              el.product_order_item_id === item.product_order_item_id &&
              el.vital_sign_group_name === item.vital_sign_group_name
            );
          });
          groupedByGroupName[item.vital_sign_group_name] = filteredData;
        });

        const allGroups = [
          { groupId: 1, key: 'temperature' },
          { groupId: 2, key: 'blood_pressure' },
          { groupId: 3, key: 'oxygen_and_pulse_rate' },
          { groupId: 4, key: 'body_composition' },
          { groupId: 5, key: 'blood_glucose' },
        ];

        const noRecordVitalSigns: { groupId: number; groupName: string }[] = [];

        allGroups.forEach((group) => {
          const exists = Object.keys(groupedByGroupName).some(
            (name) => name.replace(/\s+/g, '_').toLowerCase() === group.key
          );
          if (!exists) {
            noRecordVitalSigns.push({
              groupId: group.groupId,
              groupName: group.key,
            });
          }
        });

        state.noRecordVitalSigns = noRecordVitalSigns;
        state.vitalSignResults = _.groupBy(data, 'vital_sign_group_name');
        state.lastVitalSigns = groupedByGroupName;
      })

      .addCase(fetchVitalSignResult.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(postMeasurmentPlan.pending, (state) => {
        state.status = 'pending';
        state.type = postMeasurmentPlan.typePrefix;
      })
      .addCase(postMeasurmentPlan.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.type = postMeasurmentPlan.typePrefix;
      })
      .addCase(postMeasurmentPlan.rejected, (state, action: any) => {
        toast.error(action?.payload ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
        state.type = postMeasurmentPlan.typePrefix;
      })
      .addCase(getMeasurmentPlan.pending, (state) => {
        state.status = 'pending';
        state.type = getMeasurmentPlan.typePrefix;
      })
      .addCase(getMeasurmentPlan.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.type = getMeasurmentPlan.typePrefix;
        state.mesurmentPlan = action.payload.data;
      })
      .addCase(getMeasurmentPlan.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
        state.type = getMeasurmentPlan.typePrefix;
      })
      .addCase(updateVitalSingByUserProfile.pending, (state) => {
        state.status = 'pending';
        state.type = updateVitalSingByUserProfile.typePrefix;
      })
      .addCase(updateVitalSingByUserProfile.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.type = updateVitalSingByUserProfile.typePrefix;
      })
      .addCase(updateVitalSingByUserProfile.rejected, (state, action: any) => {
        toast.error(action?.payload?.error_text ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
        state.type = updateVitalSingByUserProfile.typePrefix;
      });
  },
});

export const { resetVitalSigns } = vitalSignsSlice.actions;

export default vitalSignsSlice.reducer;

export const selectVitalSigns = (state: RootState) => state.vitalSigns.vitalSigns;
export const selectVitalSignResults = (state: RootState) => state.vitalSigns.vitalSignResults;
export const selectLastVitalSigns = (state: RootState) => state.vitalSigns.lastVitalSigns;
export const selectVitalSignsStatus = (state: RootState) => state.vitalSigns.status;
export const selectVitalSignsType = (state: RootState) => state.vitalSigns.type;
export const selectMesurmentPlan = (state: RootState) => state.vitalSigns.mesurmentPlan;
export const selectNoRecordVitalSigns = (state: RootState) => state.vitalSigns.noRecordVitalSigns;
